/**
 * 停車場車種／計費標籤與收費分類（地圖 P 旁、彈窗標題、收費表共用）
 */

const VEHICLE_RULES = [
    { id: 'hcMoto', re: /身障機車|身心障礙機車|殘障機車/, label: '身障機車' },
    { id: 'heavy', re: /重型機車|大型重機|重機(?!車位)/, label: '重型機車' },
    { id: 'hc', re: /身障車|身障|殘障|身心障礙/, label: '身障車' },
    { id: 'large', re: /大型車|大客車|大貨車/, label: '大型車' },
    { id: 'moto', re: /機車|摩托車/, label: '機車' },
    { id: 'ev', re: /電動車|充電格位|充電位|充電樁|充電車位/, label: '電動車' },
    { id: 'car', re: /小型車|汽車|小客/, label: '小型車' }
];

const VEHICLE_ORDER = ['large', 'car', 'hc', 'moto', 'hcMoto', 'heavy', 'ev', 'other'];
const MODE_ORDER = ['hourly', 'perVisit', 'monthly'];

const VEHICLE_ICONS = {
    large: [{ cls: 'park-ico-large', text: '大', title: '大型車' }],
    car: [{ cls: 'park-ico-car', text: '車', title: '小型車' }],
    moto: [{ cls: 'park-ico-moto', text: '機', title: '機車' }],
    heavy: [{ cls: 'park-ico-heavy', text: '重', title: '重型機車' }],
    hc: [{ cls: 'park-ico-hc', text: '障', title: '身障' }],
    hcMoto: [
        { cls: 'park-ico-hc', text: '障', title: '身障' },
        { cls: 'park-ico-moto', text: '機', title: '機車' }
    ],
    ev: [{ cls: 'park-ico-ev', text: '⚡', title: '充電站／電動車充電' }],
    other: []
};

const MODE_ICONS = {
    hourly: { cls: 'park-ico-hourly', text: '時', title: '計時' },
    perVisit: { cls: 'park-ico-once', text: '次', title: '計次' },
    monthly: { cls: 'park-ico-month', text: '租', title: '月租' }
};

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function iconHtml(spec) {
    if (!spec) return '';
    return `<span class="park-ico ${spec.cls}" title="${escapeHtml(spec.title)}">${spec.text}</span>`;
}

function vehicleIconsHtml(id) {
    return (VEHICLE_ICONS[id] || []).map(iconHtml).join('');
}

export function isStructuredFeeText(text) {
    const s = String(text || '');
    return /計時|計次|月租|月繳|包月/.test(s) && /\d+\s*元/.test(s);
}

export function isCapacityProse(text) {
    const s = String(text || '');
    return /\d+\s*格/.test(s) && !isStructuredFeeText(s);
}

export function parseCapacityFromText(text) {
    const s = String(text || '');
    const num = (re) => {
        const m = s.match(re);
        return m ? parseInt(m[1], 10) || 0 : 0;
    };
    const hcMentions = [...s.matchAll(/身心障礙停車位\s*(\d+)\s*格/g)]
        .map((m) => parseInt(m[1], 10) || 0);
    const pregnancy = num(/孕婦[^。；;]*?(\d+)\s*格/)
        || num(/育有六歲以下[^。；;]*?(\d+)\s*格/);
    return {
        large: num(/大型車[：:]\s*(\d+)\s*格/),
        car: num(/小型車[：:]\s*(\d+)\s*格/),
        motorcycle: num(/機車[：:]\s*(\d+)\s*格/),
        heavy: num(/大型重機[：:]?\s*(\d+)\s*格/) || num(/重型機車[：:]?\s*(\d+)\s*格/),
        ev: num(/充電格位[：:]?\s*(\d+)\s*格/)
            || num(/電動車充電位[：:]?\s*(\d+)\s*格/)
            || num(/電動車專用車位[：:]?\s*(\d+)\s*格/)
            || num(/充電車位[：:]?\s*(\d+)\s*格/)
            || num(/充電樁\s*(\d+)\s*座/)
            || num(/充電[：:]?\s*(\d+)\s*格/),
        handicapped: hcMentions.reduce((a, b) => a + b, 0),
        pregnancy
    };
}

export function resolveParkingCapacity(props = {}) {
    const cap = props.h_capacity || {};
    const parsed = parseCapacityFromText(props.h_fees || props.fee_info || '');
    const pick = (a, b) => (Number(a) > 0 ? Number(a) : Number(b) || 0);
    return {
        large: parsed.large || 0,
        car: pick(cap.car, parsed.car),
        motorcycle: pick(cap.motorcycle, parsed.motorcycle),
        heavy: pick(cap.heavy, parsed.heavy),
        handicapped: pick(cap.handicapped, parsed.handicapped),
        pregnancy: parsed.pregnancy || 0,
        ev: pick(cap.ev, parsed.ev),
        total: Number(cap.total) || 0
    };
}

export function parkingHasMotorcycle(props = {}) {
    return inferParkingTags(props).vehicles.motorcycle === true;
}

export function parkingHasHourly(props = {}) {
    const fee = String(props.h_fees || props.fee_info || '');
    if (props.h_fee_type === 'hourly') return true;
    if (/計時|時租/.test(fee)) return true;
    return inferParkingTags(props).fees.hourly === true;
}

/** 與 inferParkingTags／徽章同一套分類；柜篩選必須對齊 */
export const PARKING_VEHICLE_FILTERS = [
    { id: 'car', label: '汽車' },
    { id: 'motorcycle', label: '機車' },
    { id: 'heavy', label: '重機' },
    { id: 'large', label: '大型' },
    { id: 'handicapped', label: '身障' }
];

export const PARKING_FEE_FILTERS = [
    { id: 'hourly', label: '計時' },
    { id: 'perVisit', label: '計次' },
    { id: 'monthly', label: '月租' }
];

function filterIdList(raw, legacyOn, legacyId) {
    const out = new Set(Array.isArray(raw) ? raw.filter(Boolean) : []);
    if (legacyOn) out.add(legacyId);
    return [...out];
}

function parkingHasVehicle(tags, id) {
    return tags?.vehicles?.[id] === true;
}

function parkingHasFee(props, tags, id) {
    if (id === 'hourly') return parkingHasHourly(props);
    return tags?.fees?.[id] === true;
}

export function parkingPassesViewFilter(props = {}, filters = {}) {
    const tags = inferParkingTags(props);
    const vehicles = filterIdList(filters.vehicles, filters.motorcycleOnly, 'motorcycle');
    const fees = filterIdList(filters.fees, filters.hourlyOnly, 'hourly');
    if (vehicles.length && !vehicles.some((id) => parkingHasVehicle(tags, id))) return false;
    if (fees.length && !fees.some((id) => parkingHasFee(props, tags, id))) return false;
    const q = String(filters.q || '').trim().toLowerCase();
    if (q) {
        const hay = [
            props.h_name,
            props.name,
            props.h_addr,
            props.address,
            props.h_district,
            props.h_city_zh
        ].join(' ').toLowerCase();
        for (const token of q.split(/\s+/).filter(Boolean)) {
            if (!hay.includes(token)) return false;
        }
    }
    return true;
}

export function inferParkingTags(props = {}) {
    const cap = resolveParkingCapacity(props);
    const fee = String(props.h_fees || props.fee_info || '');
    const feeIsRate = isStructuredFeeText(fee);
    const feeMinusSpecial = fee.replace(/重型機車|大型重機|身障機車|身心障礙機車|殘障機車/g, '');

    const vehicles = {
        large: cap.large > 0 || (feeIsRate && /大型車|大客車|大貨車/.test(fee)),
        car: cap.car > 0
            || props.has_car
            || Number(props.v_car) > 0
            || (feeIsRate && /小型車|汽車|小客/.test(fee)),
        motorcycle: cap.motorcycle > 0
            || props.has_scooter
            || Number(props.v_scooter) > 0
            || (feeIsRate && /機車|摩托車/.test(feeMinusSpecial)),
        heavy: cap.heavy > 0 || (feeIsRate && /重型機車|大型重機/.test(fee)),
        handicapped: cap.handicapped > 0
            || Number(props.v_disability) > 0
            || (feeIsRate && /身障|殘障|身心障礙/.test(fee)),
        ev: cap.ev > 0
            || props.h_ev_recharging === true
            || props.h_ev_recharging === 1
            || (Array.isArray(props.h_ev_linked_stations) && props.h_ev_linked_stations.length > 0)
            || (feeIsRate && /電動車|充電樁|充電格位|充電位/.test(fee))
            || (!feeIsRate && /電動車|充電樁|充電格位|充電位|充電車位/.test(fee))
    };

    if (!vehicles.car && !vehicles.motorcycle && !vehicles.large && !vehicles.heavy && Number(props.h_capacity?.total) > 0) {
        vehicles.car = true;
    }

    const fees = {
        hourly: feeIsRate && (props.h_fee_type === 'hourly' || /計時|時租/.test(fee)),
        monthly: feeIsRate && /月租|月繳|包月/.test(fee),
        perVisit: feeIsRate && (props.h_fee_type === 'fixed' || /計次/.test(fee))
    };

    return { vehicles, fees };
}

export function parkingBadgesHtml(tags) {
    if (!tags) return '';
    const bits = [];
    if (tags.vehicles?.large) bits.push(iconHtml(VEHICLE_ICONS.large[0]));
    if (tags.vehicles?.car) bits.push(iconHtml(VEHICLE_ICONS.car[0]));
    if (tags.vehicles?.motorcycle) bits.push(iconHtml(VEHICLE_ICONS.moto[0]));
    if (tags.vehicles?.heavy) bits.push(iconHtml(VEHICLE_ICONS.heavy[0]));
    if (tags.vehicles?.handicapped) bits.push(iconHtml(VEHICLE_ICONS.hc[0]));
    if (tags.vehicles?.ev) bits.push(iconHtml(VEHICLE_ICONS.ev[0]));
    if (tags.fees?.hourly) bits.push(iconHtml(MODE_ICONS.hourly));
    if (tags.fees?.perVisit) bits.push(iconHtml(MODE_ICONS.perVisit));
    if (tags.fees?.monthly) bits.push(iconHtml(MODE_ICONS.monthly));
    return bits.length ? `<span class="park-badges">${bits.join('')}</span>` : '';
}

export function parkingSummaryText(tags) {
    const types = [];
    if (tags?.vehicles?.large) types.push('大');
    if (tags?.vehicles?.car) types.push('🚗');
    if (tags?.vehicles?.motorcycle) types.push('🛵');
    if (tags?.vehicles?.heavy) types.push('重');
    if (tags?.vehicles?.handicapped) types.push('♿');
    if (tags?.vehicles?.ev) types.push('⚡');
    const modes = [];
    if (tags?.fees?.hourly) modes.push('時');
    if (tags?.fees?.perVisit) modes.push('次');
    if (tags?.fees?.monthly) modes.push('租');
    const modeStr = modes.length ? ` [${modes.join('/')}]` : '';
    return `${types.join('')}${modeStr}` || 'P';
}

function classifyVehicle(chunk) {
    for (const rule of VEHICLE_RULES) {
        if (rule.re.test(chunk)) return rule;
    }
    return { id: 'other', label: '其他' };
}

function classifyMode(chunk) {
    if (/月租|月繳|包月/.test(chunk)) return 'monthly';
    if (/計次/.test(chunk)) return 'perVisit';
    if (/計時|時租/.test(chunk)) return 'hourly';
    return null;
}

function parseAmounts(chunk) {
    const found = [...String(chunk).matchAll(/(\d+(?:\.\d+)?)\s*元/g)].map((m) => m[1]);
    return found;
}

function formatAmounts(list) {
    const uniq = [...new Set(list)];
    return uniq.map((n) => {
        const num = Number(n);
        if (!Number.isFinite(num)) return `${n} 元`;
        return `${num.toLocaleString('zh-Hant-TW')} 元`;
    }).join('／');
}

export function parseParkingFeeGroups(feeText) {
    const raw = String(feeText || '').trim();
    if (!raw || isCapacityProse(raw) || !isStructuredFeeText(raw)) return [];
    const chunks = raw.split(/[;；\n]+/).map((s) => s.trim()).filter(Boolean);
    if (!chunks.length) return [];

    const byVehicle = new Map();
    for (const chunk of chunks) {
        const veh = classifyVehicle(chunk);
        const mode = classifyMode(chunk);
        const amounts = parseAmounts(chunk);
        if (!mode && !amounts.length && veh.id === 'other') continue;
        if (!byVehicle.has(veh.id)) {
            byVehicle.set(veh.id, { id: veh.id, label: veh.label, modes: new Map() });
        }
        const row = byVehicle.get(veh.id);
        const key = mode || 'hourly';
        if (!row.modes.has(key)) row.modes.set(key, []);
        if (amounts.length) row.modes.get(key).push(...amounts);
        else if (!row.modes.get(key).length) row.modes.get(key).push(chunk);
    }

    return VEHICLE_ORDER
        .filter((id) => byVehicle.has(id))
        .map((id) => {
            const row = byVehicle.get(id);
            const modes = MODE_ORDER
                .filter((m) => row.modes.has(m))
                .map((m) => ({
                    mode: m,
                    text: formatAmounts(row.modes.get(m))
                }))
                .filter((m) => m.text);
            return { id: row.id, label: row.label, modes };
        })
        .filter((row) => row.modes.length);
}

export function parkingCapacityTableHtml(props = {}) {
    const cap = resolveParkingCapacity(props);
    const rows = [
        ['large', '大型車', cap.large],
        ['car', '小型車', cap.car],
        ['hc', '身障', cap.handicapped],
        ['moto', '機車', cap.motorcycle],
        ['heavy', '重型機車', cap.heavy],
        ['ev', '充電', cap.ev]
    ].filter(([, , n]) => Number(n) > 0);

    if (!rows.length) return '';

    const extra = cap.pregnancy > 0
        ? `<div class="park-cap-note">含孕婦／六歲以下兒童 ${escapeHtml(String(cap.pregnancy))} 格</div>`
        : '';

    const body = rows.map(([id, label, n]) => `
        <div class="park-cap-cell park-fee-row-${escapeHtml(id)}">
            <div class="park-fee-veh">${vehicleIconsHtml(id)}<span class="park-fee-veh-name">${escapeHtml(label)}</span></div>
            <div class="park-cap-num">${escapeHtml(n.toLocaleString('zh-Hant-TW'))} 格</div>
        </div>`).join('');

    return `<div class="park-cap-table">${body}</div>${extra}`;
}

export function parkingFeeTableHtml(feeText) {
    const groups = parseParkingFeeGroups(feeText);
    if (!groups.length) {
        if (isCapacityProse(feeText)) {
            return '<div class="park-fee-empty">此來源未提供分段費率</div>';
        }
        const fallback = escapeHtml(feeText || '暫無費率資訊');
        return `<div class="park-fee-box">${fallback}</div>`;
    }

    const rows = groups.map((g) => {
        const vehIcons = vehicleIconsHtml(g.id);
        const rates = g.modes.map((m) => {
            const ico = iconHtml(MODE_ICONS[m.mode]);
            return `<span class="park-fee-rate">${ico}<span class="park-fee-amt">${escapeHtml(m.text)}</span></span>`;
        }).join('');
        return `<div class="park-fee-row park-fee-row-${escapeHtml(g.id)}">
            <div class="park-fee-veh">${vehIcons}<span class="park-fee-veh-name">${escapeHtml(g.label)}</span></div>
            <div class="park-fee-rates">${rates}</div>
        </div>`;
    }).join('');

    return `<div class="park-fee-table">${rows}</div>`;
}
